import React, { useState, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import { Upload, RotateCcw, TriangleAlert as AlertTriangle, Target, CirclePlay as PlayCircle, Sparkles, BarChart3, FileJson } from 'lucide-react';
import pako from 'pako';
import { useUser } from '../context/UserContext';
import { ReportPracticeGuide } from '../components/ReportPracticeGuide';
import { VisualGuide } from '../components/VisualGuide';
import { ReportCharts } from '../components/ReportCharts';

type TabType = 'charts' | 'analysis' | 'visualGuide' | 'practiceGuide';

const renderCoachingMarkdown = (text: string): JSX.Element => {
  const lines = text.split('\n');
  const elements: JSX.Element[] = [];
  let listItems: string[] = [];

  const formatInline = (line: string): string =>
    line
      .replace(/\*\*(.+?)\*\*/g, '<strong class="text-white">$1</strong>')
      .replace(/\*(.+?)\*/g, '<em>$1</em>')
      .replace(/`(.+?)`/g, '<code class="bg-white/10 px-1.5 py-0.5 rounded text-sm">$1</code>');

  const flushList = () => {
    if (listItems.length > 0) {
      elements.push(
        <ul key={`list-${elements.length}`} className="list-disc list-outside pl-6 space-y-2 my-4 text-white/85">
          {listItems.map((item, i) => (
            <li key={i} className="leading-relaxed" dangerouslySetInnerHTML={{ __html: formatInline(item) }} />
          ))}
        </ul>
      );
      listItems = [];
    }
  };

  lines.forEach((line, index) => {
    if (line.startsWith('### ')) {
      flushList();
      elements.push(
        <h3 key={index} className="text-lg font-bold text-kod-accent mt-6 mb-3">
          {line.substring(4)}
        </h3>
      );
    } else if (line.startsWith('## ')) {
      flushList();
      elements.push(
        <h2 key={index} className="text-xl font-bold text-kod-accent mt-8 mb-4">
          {line.substring(3)}
        </h2>
      );
    } else if (line.startsWith('# ')) {
      flushList();
      elements.push(
        <h1 key={index} className="text-2xl font-black text-white mt-8 mb-4">
          {line.substring(2)}
        </h1>
      );
    } else if (line.match(/^[-*]\s+/)) {
      listItems.push(line.replace(/^[-*]\s+/, ''));
    } else if (line.trim() === '') {
      flushList();
      if (elements.length > 0) {
        elements.push(<div key={`space-${index}`} className="h-2" />);
      }
    } else {
      flushList();
      elements.push(
        <p
          key={index}
          className="my-3 text-white/85 leading-[1.7] text-base"
          dangerouslySetInnerHTML={{ __html: formatInline(line) }}
        />
      );
    }
  });

  flushList();
  return <>{elements}</>;
};

export const AdminVocalBlueprintDebug: React.FC = () => {
  const { isAdmin } = useUser();
  const [jsonData, setJsonData] = useState<any>(null);
  const [payload, setPayload] = useState<any>(null);
  const [error, setError] = useState<string>('');
  const [activeTab, setActiveTab] = useState<TabType>('charts');

  const [lyricsUrl, setLyricsUrl] = useState('');
  const [vocalRecordingUrl, setVocalRecordingUrl] = useState('');
  const [leadVocalUrl, setLeadVocalUrl] = useState('');
  const [finalRecordingUrl, setFinalRecordingUrl] = useState('');

  if (!isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  const handleFileUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setError('');
    const reader = new FileReader();

    reader.onload = (event) => {
      try {
        const text = event.target?.result as string;
        const parsed = JSON.parse(text);
        setPayload(parsed);
        processPayload(parsed);
      } catch (err: any) {
        setError(`Failed to parse JSON file: ${err.message}`);
      }
    };

    reader.onerror = () => {
      setError('Failed to read file');
    };

    reader.readAsText(file);
  }, []);

  const processPayload = (data: any) => {
    try {
      if (data.report_compressed_b64) {
        const binaryString = atob(data.report_compressed_b64);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        const decompressed = pako.inflate(bytes);
        const text = new TextDecoder().decode(decompressed);
        const report = JSON.parse(text);
        setJsonData(report);
        setActiveTab('charts');
      } else if (data.results || data.pitch_curves) {
        setJsonData(data);
        setActiveTab('charts');
      } else {
        setError('Payload does not contain report_compressed_b64, results, or pitch_curves. Cannot extract report data.');
      }
    } catch (err: any) {
      setError(`Failed to decompress/parse report data: ${err.message}`);
    }
  };

  const handleReset = () => {
    setJsonData(null);
    setPayload(null);
    setError('');
    setActiveTab('charts');
    setLyricsUrl('');
    setVocalRecordingUrl('');
    setLeadVocalUrl('');
    setFinalRecordingUrl('');
  };

  const coachingResponse = payload?.coaching_response || '';
  const overallScore = payload?.overall_score ? Number(payload.overall_score) : null;
  const pitchAccuracy = payload?.pitch_accuracy ? Number(payload.pitch_accuracy) : null;
  const timingAccuracy = payload?.timing_accuracy ? Number(payload.timing_accuracy) : null;
  const hasCoaching = !!(coachingResponse && coachingResponse.trim().length > 0);

  const tabs: { id: TabType; label: string; icon: React.ReactNode }[] = [
    { id: 'charts', label: 'Charts & Graphs', icon: <BarChart3 size={16} /> },
    ...(hasCoaching ? [{ id: 'analysis' as TabType, label: 'Coaching Analysis', icon: <Sparkles size={16} /> }] : []),
    { id: 'practiceGuide', label: 'Practice Guide', icon: <Target size={16} /> },
    { id: 'visualGuide', label: 'Visual Guide', icon: <PlayCircle size={16} /> },
  ];

  if (!jsonData) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-8">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-white mb-2">Vocal Blueprint Debug</h1>
          <p className="text-white/60 text-sm">
            Upload the JSON response payload from ai.thesingingleague.com to test the Vocal Blueprint rendering.
          </p>
        </div>

        {error && (
          <div className="mb-6 bg-red-500/10 border border-red-500/20 rounded-lg p-4 flex items-start gap-3">
            <AlertTriangle className="text-red-400 flex-shrink-0 mt-0.5" size={20} />
            <div>
              <p className="text-red-400 font-medium">Error</p>
              <p className="text-red-300/80 text-sm mt-1">{error}</p>
            </div>
          </div>
        )}

        <div className="bg-kod-purple-900/50 border border-kod-purple-600/40 rounded-xl p-6 mb-6">
          <label className="block mb-4">
            <span className="text-white/80 text-sm font-medium mb-2 block">Upload JSON Payload</span>
            <div className="relative">
              <input
                type="file"
                accept=".json,application/json"
                onChange={handleFileUpload}
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
              />
              <div className="flex items-center justify-center gap-3 px-6 py-12 border-2 border-dashed border-kod-purple-500/50 rounded-xl bg-kod-purple-900/30 hover:bg-kod-purple-900/50 hover:border-kod-accent/50 transition-colors">
                <FileJson size={32} className="text-kod-accent/70" />
                <div className="text-center">
                  <p className="text-white/80 font-medium">Drop a JSON file here or click to browse</p>
                  <p className="text-white/50 text-sm mt-1">Expects the full API response payload</p>
                </div>
              </div>
            </div>
          </label>
        </div>

        <div className="bg-kod-purple-900/50 border border-kod-purple-600/40 rounded-xl p-6">
          <h3 className="text-white/80 text-sm font-medium mb-4">Optional: Audio & Lyrics URLs</h3>
          <div className="space-y-3">
            <div>
              <label className="text-white/50 text-xs block mb-1">Lyrics URL (.lrc)</label>
              <input
                type="url"
                value={lyricsUrl}
                onChange={(e) => setLyricsUrl(e.target.value)}
                placeholder="https://..."
                className="w-full px-3 py-2 bg-kod-purple-900/50 border border-kod-purple-500/30 rounded-lg text-white text-sm placeholder:text-white/30 focus:outline-none focus:border-kod-accent/50"
              />
            </div>
            <div>
              <label className="text-white/50 text-xs block mb-1">Vocal Recording URL</label>
              <input
                type="url"
                value={vocalRecordingUrl}
                onChange={(e) => setVocalRecordingUrl(e.target.value)}
                placeholder="https://..."
                className="w-full px-3 py-2 bg-kod-purple-900/50 border border-kod-purple-500/30 rounded-lg text-white text-sm placeholder:text-white/30 focus:outline-none focus:border-kod-accent/50"
              />
            </div>
            <div>
              <label className="text-white/50 text-xs block mb-1">Lead Vocal URL</label>
              <input
                type="url"
                value={leadVocalUrl}
                onChange={(e) => setLeadVocalUrl(e.target.value)}
                placeholder="https://..."
                className="w-full px-3 py-2 bg-kod-purple-900/50 border border-kod-purple-500/30 rounded-lg text-white text-sm placeholder:text-white/30 focus:outline-none focus:border-kod-accent/50"
              />
            </div>
            <div>
              <label className="text-white/50 text-xs block mb-1">Final Recording URL</label>
              <input
                type="url"
                value={finalRecordingUrl}
                onChange={(e) => setFinalRecordingUrl(e.target.value)}
                placeholder="https://..."
                className="w-full px-3 py-2 bg-kod-purple-900/50 border border-kod-purple-500/30 rounded-lg text-white text-sm placeholder:text-white/30 focus:outline-none focus:border-kod-accent/50"
              />
            </div>
          </div>
        </div>

        <div className="mt-6 bg-kod-purple-900/30 border border-kod-purple-600/20 rounded-xl p-4">
          <h4 className="text-white/60 text-xs font-medium uppercase tracking-wider mb-2">Expected Payload Shape</h4>
          <pre className="text-white/50 text-xs font-mono overflow-x-auto whitespace-pre">{`{
  "id": "...",
  "performance_id": "...",
  "pitch_accuracy": "48.6",
  "timing_accuracy": "97.3",
  "note_transition_score": "52.3",
  "overall_score": "55.7",
  "coaching_response": "## Your Performance...",
  "report_compressed_b64": "eJzsvduy..."
}`}</pre>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[calc(100vh-64px)]">
      <div className="flex items-center justify-between px-6 py-3 border-b border-kod-purple-600 bg-kod-purple-900/50">
        <div className="flex items-center gap-4">
          <h1 className="text-lg font-bold text-white">Vocal Blueprint Debug</h1>
          {payload?.id && (
            <span className="text-xs text-white/40 font-mono">{payload.id}</span>
          )}
        </div>
        <button
          onClick={handleReset}
          className="flex items-center gap-2 px-4 py-2 bg-white/10 hover:bg-white/20 rounded-lg text-white/80 text-sm transition-colors"
        >
          <RotateCcw size={14} />
          Reset
        </button>
      </div>

      {error && (
        <div className="mx-4 mt-3 bg-red-500/10 border border-red-500/20 rounded-lg p-3 flex items-start gap-3">
          <AlertTriangle className="text-red-400 flex-shrink-0 mt-0.5" size={16} />
          <p className="text-red-300/80 text-sm">{error}</p>
        </div>
      )}

      <div className="flex border-b border-kod-purple-600 bg-kod-purple-900/30">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-2 px-6 py-3 text-sm font-medium transition-colors ${
              activeTab === tab.id
                ? 'text-kod-accent border-b-2 border-kod-accent bg-kod-purple-700/30'
                : 'text-white/60 hover:text-white hover:bg-kod-purple-700/20'
            }`}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      <div className={`flex-1 min-h-0 ${activeTab === 'practiceGuide' ? 'overflow-hidden' : 'overflow-auto'}`}>
        {activeTab === 'charts' && jsonData && (
          <div className="p-6">
            <ReportCharts report={jsonData} />
          </div>
        )}

        {activeTab === 'analysis' && hasCoaching && (
          <div className="max-w-3xl mx-auto px-6 py-10">
            <div className="flex items-start gap-5 mb-8">
              <img
                src="https://images.unsplash.com/photo-1580489944761-15a19d654956?ixlib=rb-1.2.1&auto=format&fit=crop&w=300&q=80"
                alt="Jocelyn"
                className="w-16 h-16 sm:w-20 sm:h-20 rounded-full object-cover border-2 border-kod-accent flex-shrink-0 shadow-lg"
              />
              <div className="min-w-0">
                <div className="text-[11px] uppercase tracking-[0.25em] text-kod-accent font-semibold mb-1">
                  Your Coach
                </div>
                <div className="text-2xl font-black text-white leading-tight">Jocelyn's Analysis</div>
                <div className="text-sm text-white/60 mt-1">A personal breakdown of your take</div>
              </div>
            </div>

            {(overallScore != null || pitchAccuracy != null || timingAccuracy != null) && (
              <div className="grid grid-cols-3 gap-3 mb-8">
                <div className="bg-kod-purple-900/50 border border-kod-purple-600/40 rounded-xl p-4 text-center">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-white/50 mb-1">Overall</div>
                  <div className="text-3xl font-black text-kod-accent">
                    {overallScore != null ? overallScore.toFixed(1) : '-'}
                  </div>
                </div>
                <div className="bg-kod-purple-900/50 border border-kod-purple-600/40 rounded-xl p-4 text-center">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-white/50 mb-1">Pitch</div>
                  <div className="text-3xl font-black text-white">
                    {pitchAccuracy != null ? pitchAccuracy.toFixed(1) : '-'}
                  </div>
                </div>
                <div className="bg-kod-purple-900/50 border border-kod-purple-600/40 rounded-xl p-4 text-center">
                  <div className="text-[10px] uppercase tracking-[0.2em] text-white/50 mb-1">Timing</div>
                  <div className="text-3xl font-black text-white">
                    {timingAccuracy != null ? timingAccuracy.toFixed(1) : '-'}
                  </div>
                </div>
              </div>
            )}

            <article className="prose prose-invert max-w-none">
              {renderCoachingMarkdown(coachingResponse)}
            </article>
          </div>
        )}

        {activeTab === 'visualGuide' && jsonData && (
          <div className="h-full">
            <VisualGuide
              report={jsonData}
              lyricsUrl={lyricsUrl || undefined}
              vocalRecordingUrl={vocalRecordingUrl || undefined}
              leadVocalUrl={leadVocalUrl || undefined}
              finalRecordingUrl={finalRecordingUrl || undefined}
            />
          </div>
        )}

        {activeTab === 'practiceGuide' && jsonData && (
          <ReportPracticeGuide
            report={jsonData}
            lyricsUrl={lyricsUrl || undefined}
            vocalRecordingUrl={vocalRecordingUrl || undefined}
            leadVocalUrl={leadVocalUrl || undefined}
          />
        )}
      </div>
    </div>
  );
};
